import {
    EmployeeConributionHistoryState,
    EmployeeConributionHistorySummaryResponse,
    EmployeePlanYearContributionResponse,
} from "@/employer/types";
import {
    FetchState,
    ListParams
} from "@/shared/types";
import * as _ from "lodash";
import * as payrollService from "@/shared/api/payrollService";
import * as storeHelper from "@/shared/storeHelper";
import { useUserStore } from "@/shared/store/user";
import { defineStore } from "pinia";

const initialState: EmployeeConributionHistoryState = {
    summary: {
        employerContributedAmount: 0,
        employerScheduledAmount: 0,
        employeeContributedAmount: 0,
        employeeScheduledAmount: 0,
        fetchState: FetchState.Uninitialized,
        employeeId: 0
    },
    payrollHistory: {
        fetchState: FetchState.Uninitialized,
        pages: [],
        pageNumber: 1,
        pageSize: 10,
        totalContributionCount: 0,
        contributions: [],
        employeeId: 0
    }
};

export const useEmployeeContributionHistoryStore = defineStore("employeeContributionHistory", {
    state: (): EmployeeConributionHistoryState => (_.cloneDeep(initialState)),
    getters: {
        summaryEeContrib: state => state.summary.employeeContributedAmount,
        summaryErContrib: state => state.summary.employerContributedAmount,
        summaryEeSched: state => state.summary.employeeScheduledAmount,
        summaryErSched: state => state.summary.employerScheduledAmount,
        summaryIsVisible: state => state.summary.fetchState === FetchState.Loaded,
        summaryTotalContrib () {
            return this.summaryEeContrib + this.summaryErContrib;
        },
        summaryTotalSched () {
            return this.summaryEeSched + this.summaryErSched;
        },
        historyIsPageLoaded: state => state.payrollHistory.fetchState === FetchState.Loaded,
        historyTotalRecords: state => state.payrollHistory.totalContributionCount,
        historyPageNumber: state => state.payrollHistory.pageNumber,
        historyPageCount: state => {
            return Math.ceil(state.payrollHistory.totalContributionCount / state.payrollHistory.pageSize);
        },
        historyPageSize: state => state.payrollHistory.pageSize,
        historyGetPlanYearContributionsByPageNumber: state => {
            const pageNumber = state.payrollHistory.pageNumber;
            const page = state.payrollHistory.pages.find(e => e.pageNumber === pageNumber);
            if (typeof page === "undefined") {
                return [];
            }
            return page.contributions;
        },
    },
    actions: {
        setSummaryFetchState (fetchState: FetchState) {
            this.summary.fetchState = fetchState;
        },
        setSummaryState (
            summaryState: EmployeeConributionHistorySummaryResponse) {
            this.summary.employerContributedAmount = summaryState.employerContributedAmount;
            this.summary.employerScheduledAmount = summaryState.employerScheduledAmount;
            this.summary.employeeContributedAmount = summaryState.employeeContributedAmount;
            this.summary.employeeScheduledAmount = summaryState.employeeScheduledAmount;
            this.summary.fetchState = FetchState.Loaded;
        },
        setHistoryFetchState (newState: FetchState): void {
            this.payrollHistory.fetchState = newState;
        },
        setPlanYearContributions (
            response: EmployeePlanYearContributionResponse): void {
            if (!this.payrollHistory.pages[response.pageNumber]) {
                this.payrollHistory.pages.push({
                    pageNumber: response.pageNumber,
                    contributions: response.contributions
                });
            }
            this.payrollHistory.pageNumber = response.pageNumber;
            this.payrollHistory.pageSize = response.pageSize;
            this.payrollHistory.totalContributionCount = response.totalContributionCount;
        },
        setPageNumber (pageNumber: number) {
            this.payrollHistory.pageNumber = pageNumber;
        },
        setSummaryEmployeeId (employeeId: number) {
            this.summary.employeeId = employeeId;
        },
        setHistoryEmployeeId (employeeId: number) {
            this.payrollHistory.employeeId = employeeId;
        },
        resetHistoryPages () {
            this.payrollHistory.pages = [];
            this.payrollHistory.pageNumber = 1;
            this.payrollHistory.pageSize = 10;
            this.payrollHistory.totalContributionCount = 0;
        },
        loadSummary (employeeId: number) {
            if (this.summary.fetchState !== FetchState.Uninitialized && employeeId === this.summary.employeeId) {
                return;
            }

            this.setSummaryFetchState(FetchState.Loading);
            const userStore = useUserStore();
            return payrollService
                .getEmployeePlanYearToDateContributionSummary(userStore.employerId, employeeId)
                .then(response => {
                    if (!response || !response.data) {
                        this.setSummaryFetchState(FetchState.Uninitialized);
                        return;
                    }

                    this.setSummaryState(response.data);
                    this.setSummaryEmployeeId(employeeId);
                })
                .catch(err => {
                    window.console.error(err);
                    this.setSummaryFetchState(FetchState.Uninitialized);
                });
        },
        getEmployeePlanYearContributions (
            { id, pageNumber, pageSize }: ListParams) {
            return new Promise<void>((resolve, reject) => {
                if (id !== this.payrollHistory.employeeId) {
                    this.resetHistoryPages();
                    pageNumber = 1;
                }
                const page = this.payrollHistory.pages.find(e => e.pageNumber === pageNumber);
                if (page) {
                    this.setPageNumber(pageNumber);
                    resolve();
                    return;
                } else {
                    this.setHistoryFetchState(FetchState.Loading);
                    const userStore = useUserStore();
                    payrollService.getEmployeePlanYearContributions(userStore.employerId, id, pageNumber)
                        .then(response => {
                            this.setHistoryEmployeeId(id);
                            this.setPlanYearContributions(response.data);
                            this.setHistoryFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
                }
            });
        }
    }
});